import { React, useState, useEffect } from 'react';

import { FormHelperText } from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import BugReportRoundedIcon from '@mui/icons-material/BugReportRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded';
import FactoryRoundedIcon from '@mui/icons-material/FactoryRounded';
import NotesRoundedIcon from '@mui/icons-material/NotesRounded';
import ReportProblemRoundedIcon from '@mui/icons-material/ReportProblemRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';

import { Alerta } from '../components/alerta';
import DayDatePicker from './dayDatePicker';
import SubmitButton from '../components/submitButton';
import { formatDateLabelPtBr, formatIsoDate } from '../utils/dateUtils';
import { fetchFlask } from '../utils/fetchFlask';

const InserirFalhaIndividual = () => {
    const [dateTime, setDateTime] = useState(null);
    const [site, setSite] = useState('');
    const [listaSites, setListaSites] = useState([]);
    const [usinas, setUsinas] = useState([]);
    const [listaUsinas, setListaUsinas] = useState([]);
    const [inversores, setInversores] = useState([]);
    const [listaInv, setListaInv] = useState([]);
    const [dados, setDados] = useState([]);
    const [natureza, setNatureza] = useState('');
    const [categoria, setCategoria] = useState('');
    const [listaCat, setListaCat] = useState([]);
    const [obs, setObs] = useState('');
    const [error, setError] = useState(false);
    const [message, setMessage] = useState('');
    const [status, setStatus] = useState('');
    const [openAlerta, setOpenAlerta] = useState(false);
    const [isSubmit, setIsSubmit] = useState(false);

    useEffect(() => {
        async function pegarDados () {
            const response = await fetchFlask('pegarDados');
            if (response.status !== 'error') {
                const dadosFetch = response.dados;
                setDados(dadosFetch);
                const dadosSite = [...new Set(dadosFetch.map(item => item[0]))];
                setListaSites(dadosSite);
            } else {
                handleAlerta(response);
            }
        };
        pegarDados();
    }, []);

    const selectSite = (e) => {
        setSite(e.target.value);
        const dadosFiltrados = dados.filter(item => item[0] === e.target.value);
        const dadosusinas = [...new Set(dadosFiltrados.map(item => item[1]))];
        dadosusinas.sort();
        dadosusinas.unshift('Selecionar Todas');
        setListaUsinas(dadosusinas);
        setUsinas([]);
        setInversores([]);
    };

    const usinasChange = (e, values) => {
        let usinasSeleciondas;
        if (values.includes('Selecionar Todas') && !usinas.includes('Selecionar Todas')) {
            let todasUsinas = [];
            todasUsinas = todasUsinas.concat(listaUsinas);
            todasUsinas.splice(0, 1);
            setUsinas(todasUsinas);
            usinasSeleciondas = todasUsinas;
        } else {
            setUsinas(values);
            usinasSeleciondas = values;
        }

        const dadosFiltrados = dados.filter(item => usinasSeleciondas.includes(item[1]));
        const dadosInv = dadosFiltrados.map(item => item[2]);
        dadosInv.sort();
        dadosInv.unshift('Selecionar Todos');
        setListaInv(dadosInv);
        setInversores([]);
    };

    const inversoresChange = (e, values) => {
        if (values.includes('Selecionar Todos') && !inversores.includes('Selecionar Todos')) {
            let todosInv = [];
            todosInv = todosInv.concat(listaInv);
            todosInv.splice(0, 1);
            setInversores(todosInv);
        } else {
            setInversores(values);
        }
    };

    const naturezaChange = (e) => {
        const naturezaAtual = e.target.value;
        setNatureza(naturezaAtual);

        const categorias = {
            Distribuidora: [
                'Interrupção de Fornecimento', 'Rompimento de Elo Fusível', 'Sobretensão', 'Subtensão',
                'Sobrecorrente', 'Variação de Frequência'],
            Clima: ['Chuva', 'Nublado', 'Vento Forte', 'Sombreamento'],
            'Interna Inversor': ['Anomalia de PV', 'Relé de Proteção', 'DPS', 'Ventilador', 'RISO CA', 'RISO CC'],
            Dispositivos: ['Disjuntor BT', 'Disjuntor MT', 'DPS CA', 'DPS CC', 'Otimizador', 'Rele Temperatura', 'Módulo', 'Transformador'],
            Manutenção: ['Corretiva', 'Preventiva', 'Preditiva'],
            Tracker: ['TCU', 'NCU'],
            Externa: ['Clientes', 'Roubo']
        };
        setListaCat(categorias[naturezaAtual]);
    };

    async function submitFalhas (e) {
        e.preventDefault();
        setIsSubmit(true);

        let data = '';
        if (dateTime) {
            setError(false);
            data = formatIsoDate(dateTime);
        } else {
            setError(true);
            setIsSubmit(false);
            return;
        }
        setError(false);

        const body = JSON.stringify({
            data,
            inversores,
            natureza,
            categoria,
            obs
        });
        try {
            const response = await fetchFlask('inserirFalhas', body);
            handleAlerta(response);
            if (response.status === 'success') {
                setUsinas([]);
                setInversores([]);
            }
        } finally {
            setIsSubmit(false);
        }
    }

    function handleAlerta (response) {
        setMessage(response.message);
        setStatus(response.status);
        setOpenAlerta(true);
    }

    const dataLabel = formatDateLabelPtBr(dateTime);
    const completedFields = [
        dateTime,
        site,
        usinas.length > 0,
        inversores.length > 0,
        natureza,
        categoria
    ].filter(Boolean).length;

    return (
        <>
            <Alerta
                texto={message}
                status={status}
                open={openAlerta}
                handleClose={() => setOpenAlerta(false)}
            />

            <form action='' method='POST' id='formFalhas' onSubmit={submitFalhas} className='ui-workflow-layout'>
                <Box className='ui-workflow-workspace'>
                    <Box className='ui-workflow-intro'>
                        <Box>
                            <h2>Registrar</h2>
                        </Box>
                        <p>
                            Informe data, site, ativos impactados e classificação da falha para catalogar a ocorrência.
                        </p>
                    </Box>

                    <Box className='ui-workflow-panel'>
                        <Box className='ui-heading'>
                            <CalendarMonthRoundedIcon />
                            <Box>
                                <span>Etapa 1</span>
                                <strong>Data e localização</strong>
                            </Box>
                        </Box>

                        <Box className='ui-fields-grid'>
                            <FormControl className='ui-control' error={error}>
                                <DayDatePicker
                                    id='data'
                                    label='Dia da falha'
                                    minDate={new Date('2023-01-02')}
                                    maxDate={new Date()}
                                    value={dateTime}
                                    onChange={setDateTime}
                                />
                                {error && (
                                    <FormHelperText>Por favor selecione uma data.</FormHelperText>
                                )}
                            </FormControl>

                            <FormControl required className='ui-control'>
                                <InputLabel id='labelSite'>Site</InputLabel>
                                <Select
                                    id='selectSite'
                                    labelId='labelSite'
                                    label='Site'
                                    value={site}
                                    onChange={selectSite}
                                >
                                    {listaSites.map((item) => (
                                        <MenuItem key={item} value={item}>{item}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Box>

                    <Box className='ui-workflow-panel'>
                        <Box className='ui-heading'>
                            <FactoryRoundedIcon />
                            <Box>
                                <span>Etapa 2</span>
                                <strong>Ativos impactados</strong>
                            </Box>
                        </Box>

                        <Box className='ui-fields-grid'>
                            <Autocomplete
                                className='failure-entry-autocomplete'
                                multiple
                                disableCloseOnSelect
                                id='usinas'
                                limitTags={8}
                                value={usinas}
                                options={listaUsinas}
                                onChange={usinasChange}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label='Usinas'
                                        required={usinas.length === 0}
                                    />
                                )}
                            />

                            <Autocomplete
                                className='failure-entry-autocomplete'
                                multiple
                                disableCloseOnSelect
                                id='inversores'
                                limitTags={8}
                                value={inversores}
                                options={listaInv}
                                onChange={inversoresChange}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label='Inversores'
                                        required={inversores.length === 0}
                                    />
                                )}
                            />
                        </Box>
                    </Box>

                    <Box className='ui-workflow-panel'>
                        <Box className='ui-heading'>
                            <ReportProblemRoundedIcon />
                            <Box>
                                <span>Etapa 3</span>
                                <strong>Classificação da falha</strong>
                            </Box>
                        </Box>

                        <Box className='ui-fields-grid'>
                            <FormControl required className='ui-control'>
                                <InputLabel id='labelNatureza'>Natureza</InputLabel>
                                <Select
                                    labelId='labelNatureza'
                                    label='Natureza'
                                    value={natureza}
                                    onChange={naturezaChange}
                                >
                                    <MenuItem value='Distribuidora'>Distribuidora</MenuItem>
                                    <MenuItem value='Clima'>Clima</MenuItem>
                                    <MenuItem value='Interna Inversor'>Interna Inversor</MenuItem>
                                    <MenuItem value='Dispositivos'>Dispositivos</MenuItem>
                                    <MenuItem value='Manutenção'>Manutenção</MenuItem>
                                    <MenuItem value='Tracker'>Tracker</MenuItem>
                                    <MenuItem value='Externa'>Externa</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl required className='ui-control'>
                                <InputLabel id='labelCategoria'>Categoria</InputLabel>
                                <Select
                                    labelId='labelCategoria'
                                    label='Categoria'
                                    value={categoria}
                                    onChange={(e) => setCategoria(e.target.value)}
                                >
                                    {listaCat.map((item) => (
                                        <MenuItem key={item} value={item}>{item}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box className='failure-entry-notes-strip'>
                            <NotesRoundedIcon />
                            <TextField
                                label='Observação'
                                id='obs'
                                multiline
                                minRows={3}
                                maxRows={6}
                                value={obs}
                                onChange={(e) => setObs(e.target.value)}
                            />
                        </Box>
                    </Box>
                </Box>

                <Box className='ui-workflow-summary'>
                    <Box className='ui-heading ui-heading-divided'>
                        <FactCheckRoundedIcon />
                        <Box>
                            <span>Resumo</span>
                            <strong>Revisão da falha</strong>
                        </Box>
                    </Box>

                    <Box className='ui-workflow-summary-list'>
                        <Box>
                            <span>Data</span>
                            <strong className={!dateTime ? 'is-empty' : undefined}>{dataLabel}</strong>
                        </Box>
                        <Box>
                            <span>Site</span>
                            <strong className={!site ? 'is-empty' : undefined}>{site || 'Não selecionado'}</strong>
                        </Box>
                        <Box>
                            <span>Usinas</span>
                            <strong>{usinas.length} selecionada{usinas.length === 1 ? '' : 's'}</strong>
                        </Box>
                        <Box>
                            <span>Inversores</span>
                            <strong>{inversores.length} selecionado{inversores.length === 1 ? '' : 's'}</strong>
                        </Box>
                        <Box>
                            <span>Classificação</span>
                            <strong>{natureza && categoria ? `${natureza} / ${categoria}` : 'Pendente'}</strong>
                        </Box>
                    </Box>

                    <Box className='ui-attention'>
                        <BugReportRoundedIcon />
                        <p>
                            Revise os ativos antes de inserir. A falha será aplicada aos inversores selecionados.
                        </p>
                    </Box>

                    <Box className='ui-attention is-ok ui-completion'>
                        <AssignmentTurnedInRoundedIcon />
                        <Box>
                            <span>Campos críticos</span>
                            <strong>{completedFields}/6 preenchidos</strong>
                        </Box>
                    </Box>

                    <SubmitButton isSubmit={isSubmit} startIcon={<SendRoundedIcon />}>
                        Inserir Falha
                    </SubmitButton>
                </Box>
            </form>
        </>
    );
};

export default InserirFalhaIndividual;
